import React from "react";
import { useParams } from "react-router-dom";

const CONTENT = {
  terms: {
    title: "Terms of Service",
    updated: "2026-02-01",
    body: [
      {
        h: "1. About FitLink Ireland",
        p: `FitLink Ireland ("we", "us", "the platform") is an online marketplace that connects members of the public ("customers") with independent, verified personal trainers ("trainers") across the Republic of Ireland. FitLink Ireland does not provide personal training services itself — trainers listed on the platform are self-employed independent professionals.`,
      },
      {
        h: "2. Accounts",
        p: `You must be at least 18 years old to register. You are responsible for keeping your account credentials secure. You agree to provide accurate information and to update it if it changes. We may suspend or terminate an account that breaches these terms or applicable law.`,
      },
      {
        h: "3. Bookings & payments",
        p: `When you book a session, you are entering into a direct contract with the trainer. The platform facilitates the booking and the payment on their behalf. All payments are processed by Stripe. FitLink Ireland charges a platform commission on each paid booking, which is deducted from the trainer's payout. Prices are inclusive of any applicable Irish VAT once the relevant threshold is crossed.`,
      },
      {
        h: "4. Cancellations & refunds",
        p: `Customers may cancel a "pending" booking free of charge before the trainer accepts it. Once a booking is confirmed and paid, the trainer's individual cancellation policy applies (shown on their profile). Refunds for disputed or undelivered sessions are handled on a case-by-case basis by the platform; contact hello@fitlink.ie within 14 days.`,
      },
      {
        h: "5. Trainer verification",
        p: `Trainers must upload valid qualifications (e.g. NCEHS, REPS Ireland or equivalent) before being approved. FitLink Ireland performs a reasonable review of these documents but does not warrant the ability, health or safety practices of any trainer. Customers train at their own risk.`,
      },
      {
        h: "6. Prohibited conduct",
        p: `You may not use the platform for illegal purposes, to harass others, to circumvent the platform fee (e.g. by moving payments off-platform), to post false reviews, or to impersonate another person. Violations may result in immediate suspension.`,
      },
      {
        h: "7. Liability",
        p: `To the maximum extent permitted by Irish law, FitLink Ireland is not liable for injuries, losses or damages arising from the actions of trainers or customers. Trainers must maintain their own public-liability and professional-indemnity insurance.`,
      },
      {
        h: "8. Governing law",
        p: `These terms are governed by the laws of the Republic of Ireland. Disputes fall under the exclusive jurisdiction of the Irish courts.`,
      },
      {
        h: "9. Contact",
        p: `Questions? Email us at hello@fitlink.ie.`,
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    updated: "2026-02-01",
    body: [
      {
        h: "1. Who we are",
        p: `FitLink Ireland is the data controller for personal data collected via this platform. We take your privacy seriously and comply with the EU General Data Protection Regulation (GDPR) and the Irish Data Protection Act 2018.`,
      },
      {
        h: "2. Data we collect",
        p: `Account data (name, email, hashed password), profile data you enter, booking history, review content, and technical data (IP, browser, device) needed to operate the service. Payment card data is handled entirely by Stripe — we never see or store your full card number.`,
      },
      {
        h: "3. Why we use it",
        p: `To provide the marketplace (matching customers with trainers), process bookings, prevent fraud, respond to support enquiries, and comply with legal obligations. We do not sell your data.`,
      },
      {
        h: "4. Data sharing",
        p: `We share the minimum necessary data with our processors: Stripe (payments), Resend/SendGrid (transactional emails), and our hosting provider. Trainers you book see your name and any notes you attach to a booking.`,
      },
      {
        h: "5. Your rights",
        p: `You may request access, correction, deletion or portability of your data at any time by emailing privacy@fitlink.ie. You may also complain to the Irish Data Protection Commission (dataprotection.ie).`,
      },
      {
        h: "6. Retention",
        p: `Account data is kept while your account is active plus 6 months. Financial records are retained for 7 years to satisfy Irish accounting obligations.`,
      },
      {
        h: "7. Security",
        p: `Passwords are hashed with bcrypt. All traffic is served over HTTPS. Access to production data is restricted.`,
      },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    updated: "2026-02-01",
    body: [
      {
        h: "1. What are cookies?",
        p: `Cookies are small text files stored on your device by websites you visit. They keep you logged in, remember your preferences and help us understand how the platform is used.`,
      },
      {
        h: "2. Cookies we use",
        p: `Strictly necessary — the "access_token" and "refresh_token" cookies keep you logged in securely. Preference — remember your search filters. We do not use tracking or advertising cookies.`,
      },
      {
        h: "3. Managing cookies",
        p: `You can clear or block cookies in your browser settings. Please note that blocking strictly-necessary cookies will prevent you logging in.`,
      },
    ],
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const c = CONTENT[slug];
  if (!c) return <div className="container-fl py-16 text-center text-neutral-500">Page not found.</div>;
  return (
    <div className="container-fl py-16 max-w-3xl">
      <div className="overline mb-2">Legal</div>
      <h1 className="text-4xl font-black tracking-tighter mb-2">{c.title}</h1>
      <p className="text-neutral-500 text-sm mb-10">Last updated: {c.updated}</p>
      <div className="space-y-8">
        {c.body.map((section, i) => (
          <section key={i}>
            <h2 className="text-lg font-bold mb-2">{section.h}</h2>
            <p className="text-neutral-700 leading-relaxed text-sm">{section.p}</p>
          </section>
        ))}
      </div>
      <div className="mt-16 border-t border-neutral-200 pt-6 text-xs text-neutral-500">
        This document is provided as a starting point for FitLink Ireland's launch. Please have it reviewed by a solicitor before relying on it for regulated activity.
      </div>
    </div>
  );
}
